import React, { useState, useMemo, memo, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Slider,
  Chip,
  FormHelperText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import BoltIcon from "@mui/icons-material/Bolt";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MultiStarIcon from "../../../assets/icons/multi_star.png";
import RightPanel from "../components/RightPanel";
import { useDispatch, useSelector } from "react-redux";
import { authStatusSelector } from "../../../services/redux/slices/authSlice";
import { setStatusPopup } from "../../../services/redux/slices/statusPopupSlice";
import {
  setBreDetails,
  setIsSuccessEligible,
  setLoanDetails,
} from "../../../services/redux/slices/newJourneyEligibilitycheckSlice";
import { Field } from "../types";
import { getValidationSchema } from "../../../utils/validationSchemas";
import Step3LoanDetailsSkeleton from "../skeletons/Step3LoanDetailsSkeleton";
import eligibilityCheckServices from "../../../services/api/service/eligibilityCheckService";
import {
  nextVerticalStep,
  setApplicantId,
  setApplicationId,
  setAssignedSMIdValue,
  setBreData,
  setBreStatus,
  setCheckIsLeadApiInvoked,
  setIsEligible,
  setLeadSequenceId,
} from "../../../services/redux/slices/stepperDynamicFormSlice";
import { setSalesUserId } from "../../../services/redux/slices/chatSlice";
import losServices from "../../../services/api/service/losService";
import StickyFooter from "../components/StickyFooter";
import Sidebar from "../components/Sidebar";

// ─── props ────────────────────────────────────────────────────────────────────

interface Step3Props {
  /** JSON field definitions for this step — driven by the API */
  fields: Field[];
  /** Step 2 field definitions — needed to build the full captureFormData payload */
  step2Fields?: Field[];
  initialValues: Partial<any>;
  onSubmit: (values: Partial<any>) => void;
  onBack: () => void;
  currentStep: number;
  progress: number;
  completedSteps: any;
}

const MIN = 1000000; // ₹10,00,000 (Total Cost of Education min)
const MAX = 50000000; // ₹5,00,00,000 (Total Cost of Education max)
const STEP_VAL = 50000;

const LOAN_MIN = 1000000; // ₹10,00,000
const LOAN_MAX = 15000000; // ₹1,50,00,000

// ─── formatters ───────────────────────────────────────────────────────────────

const formatINR = (value: number): string => {
  if (value >= 100000) {
    return `₹${(value / 100000).toFixed(value % 100000 === 0 ? 0 : 1)} L`;
  }
  return `₹${value.toLocaleString("en-IN")}`;
};

const formatINRFull = (value: number): string =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);

// ─── shared slider sx ─────────────────────────────────────────────────────────

const sliderSx = {
  color: "primary.main",
  height: 6,
  "& .MuiSlider-thumb": {
    width: 22,
    height: 22,
    bgcolor: "white",
    border: "3px solid",
    borderColor: "primary.main",
    boxShadow: "0 2px 8px rgba(27,107,90,0.3)",
    "&:hover": { boxShadow: "0 3px 12px rgba(27,107,90,0.4)" },
  },
  "& .MuiSlider-track": {
    background: "linear-gradient(90deg, #1B6B5A, #2A8A74)",
    border: "none",
  },
  "& .MuiSlider-rail": { bgcolor: "grey.200" },
  "& .MuiSlider-valueLabel": {
    bgcolor: "primary.main",
    borderRadius: 1.5,
    fontSize: "0.72rem",
    fontWeight: 700,
  },
};

// ─── Continue disable logic ───────────────────────────────────────────────────
//
//  1. loanAmount must be ≥ LOAN_MIN and ≤ LOAN_MAX
//  2. totalCost must be ≥ MIN and ≤ MAX
//  3. loanAmount must not exceed totalCost

const isSubmitDisabled = (loanAmount: number, totalCost: number): boolean => {
  if (loanAmount < LOAN_MIN) return true;
  if (loanAmount > LOAN_MAX) return true;
  if (totalCost < MIN) return true;
  if (totalCost > MAX) return true;
  if (loanAmount > totalCost) return true; // loan cannot exceed total education cost
  return false;
};

// ─── helper: resolve object or string to its value string ────────────────────

const extractValue = (field: any): string => {
  if (!field) return "";
  if (typeof field === "string") return field;
  if (typeof field === "object" && field.value) return field.value;
  return "";
};

const extractCode = (field: any): string => {
  if (!field) return "";
  if (typeof field === "object" && field.code) return field.code;
  return "";
};

// Extracts the code from a DropdownOptionValue object, or the leading segment
// before the first "-" from a label string (e.g. "1119-CALIFORNIA..." → "1119")
const extractValueCode = (field: any): string => {
  if (!field) return "";
  if (typeof field === "object" && "code" in field) return field.code ?? "";
  if (typeof field === "string") {
    const match = field.match(/^\s*([^-\s]+)\s*(?:-|$)/);
    return match ? match[1] : field.trim();
  }
  return "";
};

// ─── main component ───────────────────────────────────────────────────────────

const Step3LoanDetails: React.FC<Step3Props> = ({
  fields,
  step2Fields = [],
  initialValues,
  onSubmit,
  currentStep,
  onBack,
  progress,
  completedSteps
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const dispatch = useDispatch();
  const isAuthenticated = useSelector(authStatusSelector);
  const trackingId = useSelector(
    (state: any) => state?.eligibilityCheck?.trackingId ?? "",
  );

  // ── Read Step 1 and Step 2 data from Redux for BRE payload ───────────────────

  const basicDetails = useSelector(
    (state: any) => state?.eligibilityCheck?.stepFields?.basicDetails,
  );
  const courseDetails = useSelector(
    (state: any) => state?.eligibilityCheck?.stepFields?.courseDetails,
  );

  // ── Redux selectors needed by handleLeadCreationFlow ─────────────────────────
  const checkIsLeadApiInvoked = useSelector(
    (state: any) => state?.stepper?.checkIsLeadApiInvoked,
  );
  const applicationSource = useSelector(
    (state: any) => state?.stepper?.applicationSource ?? "",
  );
  const assignedSMIdValue = useSelector(
    (state: any) => state?.stepper?.assignedSMIdValue ?? "",
  );

  // ── Slider state ─────────────────────────────────────────────────────────────

  const [loanAmount, setLoanAmount] = useState<number>(
    typeof initialValues?.loanAmount === "number"
      ? initialValues.loanAmount
      : 1500000,
  );
  const [isBtnLoading, setIsBtnLoading] = useState(false);

  const [totalCost, setTotalCost] = useState<number>(
    typeof initialValues?.totalCost === "number"
      ? initialValues.totalCost
      : 3000000,
  );

  const [submitAttempted, setSubmitAttempted] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  // ── Field lookup from JSON ────────────────────────────────────────────────────
  // Labels, ids, and names come from the JSON rather than being hardcoded.

  const loanAmountField = useMemo(
    () => fields?.find((f) => f.name === "loanAmount"),
    [fields],
  );
  const totalCostField = useMemo(
    () => fields?.find((f) => f.name === "totalCost"),
    [fields],
  );

  const validationSchema = useMemo(
    () => getValidationSchema(fields, 0, 2),
    [fields],
  );

  const loanExceedsTotalCost = loanAmount > totalCost;
  const loanBelowMin = loanAmount < LOAN_MIN;
  const loanAboveMax = loanAmount > LOAN_MAX;
  const totalCostBelowMin = totalCost < MIN;
  const totalCostAboveMax = totalCost > MAX;

  const submitDisabled = isSubmitDisabled(loanAmount, totalCost);

  const scrollToFirstError = useCallback((): void => {
    if (!formRef.current) return;

    setTimeout(() => {
      const container = formRef.current;
      if (!container) return;

      // Check loan amount errors first (AC4 priority)
      const loanHasError = loanBelowMin || loanAboveMax || loanExceedsTotalCost;
      const totalCostHasError = totalCostBelowMin || totalCostAboveMax;

      const firstErrorEl = loanHasError
        ? (container.querySelector(
            "[data-field-name='loanAmount']",
          ) as HTMLElement | null)
        : totalCostHasError
          ? (container.querySelector(
              "[data-field-name='totalCost']",
            ) as HTMLElement | null)
          : null;

      if (firstErrorEl) {
        firstErrorEl.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  }, [
    loanBelowMin,
    loanAboveMax,
    loanExceedsTotalCost,
    totalCostBelowMin,
    totalCostAboveMax,
  ]);

  const handleSubmitClick = useCallback((): void => {
    setSubmitAttempted(true);

    if (submitDisabled) {
      scrollToFirstError();
      return;
    }

    void handleSubmit();
  }, [submitDisabled, scrollToFirstError]);

  const handleSubmit = async (): Promise<void> => {
    if (submitDisabled) return;

    const submittedValues = {
      loanAmount,
      totalCost,
    };
    setIsBtnLoading(true);

    // ── Helper: resolve a Step 2 field value from courseDetails ──────────────
    const resolveStep2Value = (fieldName: string): any => {
      const cd = courseDetails;
      if (!cd) return "";

      switch (fieldName) {
        case "courseCategory":
          return cd.courseCategory ?? "";

        case "admissionStatus":
          return cd.admissionStatus ?? "";

        // country → display label (e.g. "UNITED STATES OF AMERICA")
        case "country":
          return (
            cd.countryLabel ||
            (typeof cd.country === "object" ? cd.country?.label : "") ||
            ""
          );

        // universityCountryId → country ID (e.g. "US")
        case "universityCountryId":
          return (
            cd.selectedCountryId ||
            (typeof cd.country === "object" ? cd.country?.value : cd.country) ||
            ""
          );

        // universityCampusId → university CODE (e.g. "1119"), "" when notInList
        case "universityCampusId":
          if (cd.universityNotInList) return "";
          return (
            extractCode(cd.university) ||
            (typeof cd.university === "object" ? cd.university?.code : "") ||
            ""
          );

        // university → "code-label-id" when from list (e.g. "1119-CALIFORNIA STATE...-357")
        //            → free text string when notInList
        case "university": {
          if (cd.universityNotInList) {
            return typeof cd.university === "string" ? cd.university : "";
          }
          const uniLabel =
            cd.universityLabel ||
            (typeof cd.university === "object"
              ? cd.university?.label
              : cd.university) ||
            "";
          return uniLabel;
        }

        case "universityNotInList":
          return cd.universityNotInList ?? false;

        // institute_code → institute CODE (e.g. "1278"), "" when notInList
        case "institute_code":
          if (cd.instituteNotInList) return "";
          return (
            extractCode(cd.institute) ||
            (typeof cd.institute === "object" ? cd.institute?.code : "") ||
            cd.institute_code ||
            ""
          );

        // institute → label (e.g. "1278-CALIFORNIA STATE...") when from list
        //           → free text string when notInList
        case "institute":
          return cd.instituteNotInList
            ? typeof cd.institute === "string"
              ? cd.institute
              : ""
            : cd.instituteLabel ||
                (typeof cd.institute === "object"
                  ? cd.institute?.label
                  : cd.institute) ||
                "";

        case "instituteNotInList":
          return cd.instituteNotInList ?? false;

        // courseCode → course CODE (e.g. "10465"), "" when notInList
        case "courseCode":
          if (cd.courseNameNotInList) return "";
          return extractCode(cd.courseName) || cd.courseCode || "";

        // courseName → label (e.g. "10465-MBA E PROGRAM") when from list
        //            → free text string when notInList
        case "courseName":
          return cd.courseNameNotInList
            ? (cd.courseName ?? "")
            : cd.courseNameLabel || cd.courseName || "";

        case "courseNameNotInList":
          return cd.courseNameNotInList ?? false;

        case "courseType":
          return cd.courseType ?? "";

        default:
          return (cd as any)[fieldName] ?? "";
      }
    };

    // ── Step 2 data entries ───────────────────────────────────────────────────
    // Built directly from Redux courseDetails using known field IDs (12–25).
    // This avoids dependency on step2Fields prop being populated at render time.
    const STEP2_FIELD_MAP: Array<{ field_id: number; field_key: string }> = [
      { field_id: 12, field_key: "courseCategory" },
      { field_id: 13, field_key: "admissionStatus" },
      { field_id: 14, field_key: "country" },
      { field_id: 15, field_key: "universityCountryId" },
      { field_id: 16, field_key: "universityCampusId" },
      { field_id: 17, field_key: "university" },
      { field_id: 18, field_key: "universityNotInList" },
      { field_id: 19, field_key: "institute_code" },
      { field_id: 20, field_key: "institute" },
      { field_id: 21, field_key: "instituteNotInList" },
      { field_id: 22, field_key: "courseCode" },
      { field_id: 23, field_key: "courseName" },
      { field_id: 24, field_key: "courseNameNotInList" },
      { field_id: 25, field_key: "courseType" },
    ];

    const step2Data = STEP2_FIELD_MAP.map(({ field_id, field_key }) => ({
      field_id,
      field_key,
      field_value: resolveStep2Value(field_key),
    }));

    // ── Step 3 data entries ───────────────────────────────────────────────────
    const step3Data = fields
      .filter((f) => !f.isRemoved)
      .map((field) => ({
        field_id: field.id,
        field_key: field.name,
        field_value: String(
          field.name === "loanAmount"
            ? loanAmount
            : field.name === "totalCost"
              ? totalCost
              : "",
        ),
      }));

    // ── Combined payload ──────────────────────────────────────────────────────
    const data = [...step2Data, ...step3Data];

    try {
      // ── Step 1: Capture form data ─────────────────────────────────────────

      const capturePayload: any = {
        formData: data,
        submissionTrackingId: trackingId,
      };

      const captureResp =
        await eligibilityCheckServices.captureFormData(capturePayload);

      if (captureResp?.data?.status !== "success") {
        dispatch(
          setStatusPopup({
            status: "error",
            message: captureResp?.data?.message || "Capture failed",
          }),
        );
        return;
      }

      // ── Step 2: BRE / offer check ─────────────────────────────────────────
      // Build the BRE payload from Redux slice data.
      // country/university/institute may be DropdownOptionValue objects or strings.

      const universityCode =
        extractCode(courseDetails?.university) ||
        courseDetails?.universityCampusId ||
        "";

      const instituteCode =
        courseDetails?.institute_code ||
        extractCode(courseDetails?.institute) ||
        "";

      const courseCode =
        courseDetails?.courseCode ||
        extractCode(courseDetails?.courseCode) ||
        "";

      const brePayload = {
        applicationId: trackingId,
        provisionalOfferBody: {
          values: {
            input: {
              Unique_Lead_ID: trackingId,
              oApplicant: {
                oApplName: {
                  sFirstName: basicDetails?.firstName || "",
                  sLastName: basicDetails?.lastName || "",
                },
                aCourseDetails: [
                  {
                    // Include institute_code only when NOT in free-text mode
                    // ...(!courseDetails?.instituteNotInList &&
                    //   instituteCode && {
                    //     institute_code: instituteCode,
                    //   }),
                    institute_code: instituteCode,

                    // Include course_code only when NOT in free-text mode
                    // ...(!courseDetails?.courseNameNotInList &&
                    //   courseCode && {
                    //     course_code: courseCode,
                    //   }),
                    course_code: courseCode,

                    // Include university_campus_id only when NOT in free-text mode
                    ...(!courseDetails?.universityNotInList && {
                      university_campus_id: universityCode,
                    }),

                    university_country_id:
                      extractValue(courseDetails?.country) ||
                      courseDetails?.universityCountryId ||
                      "",

                    category: courseDetails?.courseType || "",
                    admission_status: courseDetails?.admissionStatus || "",
                    course_category:
                      courseDetails?.courseCategory === "PG"
                        ? "Post Graduation"
                        : courseDetails?.courseCategory || "",
                  },
                ],
              },
            },
          },
        },
      };

      let breResp: any = null;
      try {
        breResp = await eligibilityCheckServices.checkOffer(brePayload);

        dispatch(
          setIsSuccessEligible(breResp?.data?.data?.Scheme_Available == "Yes"),
        );
        dispatch(setLoanDetails(submittedValues));
        dispatch(setBreStatus("FROM_FORM"));
        dispatch(setBreData(breResp?.data?.data));
        dispatch(setBreDetails(breResp?.data?.data));
        dispatch(setIsEligible(breResp?.data?.data?.Scheme_Available == "Yes"));
        dispatch(nextVerticalStep());

        if (breResp?.data?.data?.Scheme_Available.toLowerCase() === "yes") {
          const partnerLeadPayload: {
            applicationId: any;
            leadFirstName: any;
            leadLastName: any;
            leadMobileNo: any;
            leadEmailAddr: any;
            amountRequested: any;
            admissionStatus: any;
            countryOfStudyId: any;
            universityId: string;
            universityNotInList: any;
            otherUniversityName: any;
            instituteId: string;
            instituteNotInList: any;
            otherInstituteName: any;
            courseId: string;
            courseNotInList: any;
            leadCourseTypeName: any;
            stemFlag: boolean;
            otherCourseName: string;
            assignedSMId?: string;
            pincode?: string;
          } = {
            pincode: basicDetails?.pincode || "",
            applicationId: trackingId || "",
            leadFirstName: basicDetails?.firstName || "",
            leadLastName: basicDetails?.lastName || "",
            leadMobileNo: basicDetails?.phone_number,
            leadEmailAddr: basicDetails?.email || "",
            amountRequested: submittedValues.loanAmount?.toString() || "",
            admissionStatus: courseDetails?.admissionStatus || "",
            countryOfStudyId: extractValue(courseDetails?.country) || "US",

            universityId: courseDetails?.universityNotInList
              ? ""
              : courseDetails?.university != null &&
                  courseDetails?.university !== ""
                ? String(extractValueCode(courseDetails?.university))
                : "",
            universityNotInList: courseDetails?.universityNotInList || false,
            otherUniversityName: courseDetails?.universityNotInList
              ? typeof courseDetails?.university === "string"
                ? courseDetails.university
                : ""
              : "",

            instituteId: courseDetails?.instituteNotInList
              ? ""
              : courseDetails?.institute != null &&
                  courseDetails?.institute !== ""
                ? String(extractValueCode(courseDetails?.institute))
                : "",
            instituteNotInList: courseDetails?.instituteNotInList || false,
            otherInstituteName: courseDetails?.instituteNotInList
              ? typeof courseDetails?.institute === "string"
                ? courseDetails.institute
                : ""
              : "",

            courseId: courseDetails?.courseNameNotInList
              ? ""
              : courseDetails?.courseName != null &&
                  courseDetails?.courseName !== ""
                ? String(extractValueCode(courseDetails?.courseName))
                : "",
            courseNotInList: courseDetails?.courseNameNotInList || false,
            leadCourseTypeName:
              courseDetails?.courseCategory === "PG"
                ? "Post Graduate"
                : courseDetails?.courseCategory || "",
            stemFlag: courseDetails?.courseType === "STEM",
            otherCourseName: courseDetails?.courseNameNotInList
              ? String(extractValue(courseDetails?.courseName))
              : "",
          };

          handleLeadCreationFlow(partnerLeadPayload, "", submittedValues);
        } else {
          setIsBtnLoading(false);
          onSubmit(submittedValues);
        }
      } catch (e: any) {
        const apiMessage =
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Unable to check eligibility. Please try again.";

        dispatch(
          setStatusPopup({
            status: "error",
            message: apiMessage,
          }),
        );
      }
    } catch (error) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: "An error occurred while capturing form data",
        }),
      );
    }
  };

  const handleLeadCreationFlow = async (
    partnerLeadPayload: any,
    leadSequenceId: string,
    submittedValues: any,
  ) => {
    if (checkIsLeadApiInvoked?.ASSIGN_SM == false && applicationSource == "") {
      const payload = {
        pinCode: basicDetails?.pincode,
        applicationId: trackingId,
      };
      await eligibilityCheckServices
        .leadAllocation(payload)
        .then((res: any) => {
          partnerLeadPayload.assignedSMId =
            String(res?.data?.data?.assignedSMId) || "";
          dispatch(setAssignedSMIdValue(String(res?.data?.data?.assignedSMId)));
          dispatch(setSalesUserId(String(res?.data?.data?.assignedSMId)));
          dispatch(
            setCheckIsLeadApiInvoked({
              key: "ASSIGN_SM",
              value: true,
            }),
          );
          setIsBtnLoading(false);
        })
        .catch((err: any) => {
          const errMessage =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            "Lead allocation failed. Please try again.";
          dispatch(
            setStatusPopup({
              status: "error",
              message: errMessage,
            }),
          );
          setIsBtnLoading(false);
          return;
        });
    }

    let leadIdData = "";

    if (
      checkIsLeadApiInvoked?.CREATE_LEAD == false &&
      applicationSource == ""
    ) {
      const hasLeadSequenceId =
        !!leadSequenceId &&
        String(leadSequenceId).trim().toLowerCase() !== "null";

      if (!hasLeadSequenceId) {
        try {
          partnerLeadPayload.leadPincode = basicDetails?.pincode;
          partnerLeadPayload.assignedSMId =
            partnerLeadPayload.assignedSMId || String(assignedSMIdValue);
          await eligibilityCheckServices
            .createPartnerLead(partnerLeadPayload)
            .then(async (resp: any) => {
              leadIdData = resp?.data?.data?.leadSequence.toString();
              dispatch(
                setLeadSequenceId(resp?.data?.data?.leadSequence.toString()),
              );
              if (resp?.status == 200) {
                dispatch(
                  setCheckIsLeadApiInvoked({
                    key: "CREATE_LEAD",
                    value: true,
                  }),
                );
              }
              onSubmit(submittedValues);
            })
            .catch(() => {
              return;
            });
        } catch (error: any) {
          console.error("Lead to Application Error:", error);
          dispatch(
            setStatusPopup({
              status: "error",
              message: "Lead to Application Error. Please try again.",
            }),
          );
          return;
        }
      } else {
        try {
          try {
            await losServices.getDataByLeadId(trackingId ?? "");
          } catch (getDataError) {
            console.error("Get Data By Lead Id Error:", getDataError);
          }
          const updateLeadPayload = {
            trackingId: trackingId ?? "",
          };
          const updateLeadResp: any =
            await losServices.updateLead(updateLeadPayload);
          if (updateLeadResp?.status === 200) {
            leadIdData = String(leadSequenceId);
            dispatch(
              setCheckIsLeadApiInvoked({
                key: "CREATE_LEAD",
                value: true,
              }),
            );
          }
        } catch (error: any) {
          console.error("Update Lead Error:", error);
          return;
        }
      }
    }
    setIsBtnLoading(false);

    // lead id
    if (checkIsLeadApiInvoked?.LEAD_TO_APPLICATION == false) {
      try {
        if (leadSequenceId || leadIdData) {
          const leadToAppPayload = {
            leadId: leadSequenceId || leadIdData,
            trackingId: trackingId ?? "",
          };

          const leadToAppResp: any =
            await losServices.leadToApplication(leadToAppPayload);
          if (leadToAppResp.status == 200) {
            dispatch(
              setCheckIsLeadApiInvoked({
                key: "LEAD_TO_APPLICATION",
                value: true,
              }),
            );
            dispatch(
              setApplicationId(leadToAppResp?.data?.data?.applicationId),
            );
            dispatch(setApplicantId(leadToAppResp?.data?.data?.applicantId));
          }
        }
      } catch (error: any) {
        console.error("Lead to Application Error:", error);
        const errMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Lead to Application Error. Please try again.";
        dispatch(
          setStatusPopup({
            status: "error",
            message: errMessage,
          }),
        );
      }
    }
  };

  // ─── render ───────────────────────────────────────────────────────────────────

  if (!fields || fields.length === 0) {
    return <Step3LoanDetailsSkeleton />;
  }

  const showLoanErrors = submitAttempted;
  const loanAmountError = showLoanErrors
    ? loanBelowMin || loanAboveMax
      ? "Please enter the required loan amount."
      : loanExceedsTotalCost
        ? "Required Loan Amount cannot be greater than Total Cost of Education."
        : undefined
    : loanExceedsTotalCost
      ? "Required Loan Amount cannot be greater than Total Cost of Education."
      : undefined;

  const totalCostError =
    showLoanErrors && (totalCostBelowMin || totalCostAboveMax)
      ? "Please enter the total education cost."
      : undefined;

  return (
    <Box
      ref={formRef}
      sx={{ display: isMobile ? "column" : "flex", minWidth: 0, gap: 3 }}
    >
      <Box sx={{ width: "100%" }}>
        {/* ── Header ── */}
        <Box sx={{ mb: 4, bgcolor: isMobile ? "#EBF5F6" : "", p: { xs: 2 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <img
              src={MultiStarIcon}
              style={{ width: "13px", height: "13px" }}
              alt=""
            />
            <Typography
              variant="body2"
              sx={{ color: "#251A00", fontWeight: 600 }}
            >
              Loan Details
            </Typography>
          </Box>
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: "1.6rem", md: "42px" },
              fontWeight: 700,
              lineHeight: 1.3,
              mb: 1,
            }}
          >
            Let's secure{" "}
            <Box component="span" sx={{ color: "primary.main" }}>
              your offer
            </Box>
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", lineHeight: 1.6 }}
          >
            We'll match your academic ambitions with the most competitive
            education loan rates available.
          </Typography>
        </Box>

        {/* ── Mobile Sidebar ── */}
        {isMobile && (
          <Sidebar
            currentStep={currentStep === 4 ? 3 : currentStep}
            completedSteps={completedSteps}
          />
        )}

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 4 },
            borderRadius: "16px",
            bgcolor: "#ffffff",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* ── Badges ── */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mb: 4,
              flexWrap: "wrap",
              justifyContent: isMobile ? "start" : "end",
            }}
          >
            <Chip
              icon={
                <StarOutlineIcon
                  sx={{ fontSize: 16, color: "#000 !important" }}
                />
              }
              label="100% Financing Available"
              size="small"
              sx={{
                bgcolor: "#FFDF94",
                color: "#251A00",
                fontWeight: 600,
                padding: { xs: "14px 6px", sm: "16px 10px" },
                fontSize: { xs: "9px", sm: "10.5px" },
                border: "1px solid #F5E5B8",
              }}
            />
            <Chip
              icon={
                <BoltIcon sx={{ fontSize: 16, color: "#000 !important" }} />
              }
              label="Instant Approval"
              size="small"
              sx={{
                bgcolor: "#7EF6EE",
                color: "#251A00",
                fontWeight: 600,
                border: "1px solid rgba(27,107,90,0.2)",
                padding: { xs: "14px 6px", sm: "16px 10px" },
                fontSize: { xs: "9px", sm: "10.5px" },
              }}
            />
          </Box>

          {/* ── Loan Amount slider ── */}
          <Box sx={{ mb: 5 }} data-field-name="loanAmount">
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "20px",
                color: "text.primary",
                mb: 2.5,
              }}
            >
              {loanAmountField?.label ?? "Required Loan Amount"}
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "start", sm: "center" },
                mb: 3,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  px: 4,
                  py: 1,
                  borderRadius: 2,
                  border: "1.5px solid",
                  borderColor: loanAmountError ? "error.main" : "primary.main",
                  bgcolor: "#0067630D",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.2rem",
                    color: loanAmountError ? "error.main" : "#006763",
                  }}
                >
                  {formatINRFull(loanAmount)}
                </Typography>
              </Paper>
            </Box>

            <Box sx={{ px: { xs: 1, md: 2 } }}>
              <Slider
                value={loanAmount}
                onChange={(_, val) => setLoanAmount(val as number)}
                min={LOAN_MIN}
                max={LOAN_MAX}
                step={STEP_VAL}
                valueLabelDisplay="auto"
                valueLabelFormat={formatINRFull(loanAmount)}
                sx={{
                  ...sliderSx,
                  ...(loanAmountError
                    ? {
                        color: "error.main",
                        "& .MuiSlider-thumb": {
                          ...sliderSx["& .MuiSlider-thumb"],
                          borderColor: "error.main",
                        },
                      }
                    : {}),
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 0.5,
                }}
              >
                <Typography
                  sx={{ fontSize: "0.72rem", color: "text.secondary" }}
                >
                  {formatINRFull(LOAN_MIN)}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.72rem", color: "text.secondary" }}
                >
                  {formatINRFull(LOAN_MAX)}
                </Typography>
              </Box>
            </Box>

            {/* ── Loan amount error message ── */}
            {loanAmountError && (
              <FormHelperText error sx={{ fontSize: "0.78rem", mt: 1, ml: 2 }}>
                {loanAmountError}
              </FormHelperText>
            )}
          </Box>

          {/* ── Total Cost of Education slider ── */}
          <Box sx={{ mb: 5 }} data-field-name="totalCost">
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "20px",
                color: "text.primary",
                mb: 2.5,
              }}
            >
              {totalCostField?.label ?? "Total Cost of Education"}
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "start", sm: "center" },
                mb: 3,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  px: 4,
                  py: 1,
                  borderRadius: 2,
                  border: "1.5px solid",
                  borderColor: totalCostError ? "error.main" : "primary.main",
                  bgcolor: "#0067630D",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.2rem",
                    color: totalCostError ? "error.main" : "#006763",
                  }}
                >
                  {formatINRFull(totalCost)}
                </Typography>
              </Paper>
            </Box>

            <Box sx={{ px: { xs: 1, md: 2 } }}>
              <Slider
                value={totalCost}
                onChange={(_, val) => setTotalCost(val as number)}
                min={MIN}
                max={MAX}
                step={STEP_VAL}
                valueLabelDisplay="auto"
                valueLabelFormat={formatINRFull(totalCost)}
                sx={{
                  ...sliderSx,
                  ...(totalCostError
                    ? {
                        color: "error.main",
                        "& .MuiSlider-thumb": {
                          ...sliderSx["& .MuiSlider-thumb"],
                          borderColor: "error.main",
                        },
                      }
                    : {}),
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 0.5,
                }}
              >
                <Typography
                  sx={{ fontSize: "0.72rem", color: "text.secondary" }}
                >
                  {formatINRFull(MIN)}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.72rem", color: "text.secondary" }}
                >
                  {formatINRFull(MAX)}
                </Typography>
              </Box>
            </Box>

            {/* ── Total cost error message ── */}
            {totalCostError && (
              <FormHelperText error sx={{ fontSize: "0.78rem", mt: 1, ml: 2 }}>
                {totalCostError}
              </FormHelperText>
            )}
          </Box>

          {/* Actions */}
          {!isMobile && (
            <Box sx={{ display: "flex", justifyContent: "end", mt: 3 }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={onBack}
                  sx={{ flex: 1, py: 1.75, fontSize: "1rem", fontWeight: 600 }}
                >
                  Previous
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmitClick}
                  disabled={isBtnLoading}
                  endIcon={<ArrowForwardIcon />}
                  fullWidth
                  sx={{
                    flex: 2,
                    py: 1.75,
                    fontSize: "1rem",
                    fontWeight: 700,
                    minWidth: 270,
                  }}
                >
                  {isBtnLoading ? "Loading..." : "Submit Application"}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Desktop Right Panel */}
      {currentStep <= 3 && (
        <Box
          sx={{
            flexShrink: 0,
            bgcolor: "#EBF5F6",
            pl: isMobile ? 2 : 2,
            pr: isMobile ? 2 : 0,
            py: 3,
            position: "sticky",
            top: 64,
          }}
        >
          <RightPanel
            currentStep={currentStep}
            isShowInfo
          />
        </Box>
      )}

      {isMobile && (
        <StickyFooter progress={progress} onBack={onBack} onContinue={handleSubmitClick} />
      )}
    </Box>
  );
};

export default memo(Step3LoanDetails);
